import { useQuery } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  Send,
  CheckCircle,
  Eye,
  AlertCircle,
  MessageSquare,
  Phone,
  Calendar,
  Loader2,
  MapPin,
} from 'lucide-react';
import { useState } from 'react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

function formatWeekRange(weekStart, weekEnd) {
  const fmt = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const end = new Date(weekEnd + 'T00:00:00');
  end.setDate(end.getDate() - 1);
  return `${fmt(weekStart)} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

export default function ReminderLogs() {
  const [historyFilter, setHistoryFilter] = useState(null);
  const [historyPage, setHistoryPage] = useState(0);
  const { currentLocation } = useLocationContext();

  const { data: history, isLoading } = useQuery({
    queryKey: ['history', historyFilter, historyPage],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50', offset: String(historyPage * 50) });
      if (historyFilter) params.set('status', historyFilter);
      const { data } = await axios.get(`/api/reminders/history?${params}`);
      return data;
    },
  });

  const messages = history?.messages || [];
  const totalMessages = history?.total || 0;

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const s = isoString.endsWith('Z') ? isoString : isoString.replace(' ', 'T') + 'Z';
    const d = new Date(s);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      sent: { color: 'bg-blue-100 text-blue-700', icon: Send },
      delivered: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      read: { color: 'bg-emerald-100 text-emerald-700', icon: Eye },
      failed: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
    };
    const c = config[status] || config.sent;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium ${c.color}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  return (
    <Layout>
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <MessageSquare className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Reminder Logs</h1>
            <p className="text-xs text-brand-neutral hidden md:block">WhatsApp message delivery logs</p>
          </div>
        </div>
        {currentLocation && (
          <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
            <MapPin className="w-3 h-3" />
            {currentLocation.name}
          </span>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6">
        <div className="max-w-5xl mx-auto space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light overflow-hidden">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light bg-brand-light/30 flex items-center justify-between">
              <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Message Logs ({totalMessages})
              </h3>
              <div className="flex gap-1.5">
                {['sent', 'delivered', 'read', 'failed'].map((s) => (
                  <button
                    key={s}
                    onClick={() => { setHistoryFilter(historyFilter === s ? null : s); setHistoryPage(0); }}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${historyFilter === s ? 'bg-brand-primary text-white' : 'bg-brand-light text-brand-neutral hover:bg-brand-primary/10'
                      }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-primary" />
              </div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center text-brand-neutral text-sm">No messages found</div>
            ) : (
              <>
                <div className="divide-y divide-brand-light/50">
                  {messages.map((msg) => (
                    <div key={msg.id} className="p-3 md:p-4 hover:bg-brand-light/30 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getStatusBadge(msg.status)}
                            {msg.message_type && (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                msg.message_type === 'booking_reminder' ? 'bg-blue-50 text-blue-600'
                                  : msg.message_type === 'booking_confirmation' ? 'bg-emerald-50 text-emerald-600'
                                  : msg.message_type === 'slot_change' ? 'bg-purple-50 text-purple-600'
                                  : msg.message_type === 'tomorrow_alert' ? 'bg-amber-50 text-amber-600'
                                  : msg.message_type === 'today_alert' ? 'bg-green-50 text-green-600'
                                  : 'bg-gray-50 text-gray-600'
                              }`}>
                                {msg.message_type === 'booking_reminder' ? 'Booking Reminder'
                                  : msg.message_type === 'booking_confirmation' ? 'Booking Confirmation'
                                  : msg.message_type === 'slot_change' ? 'Slot Change'
                                  : msg.message_type === 'tomorrow_alert' ? 'Tomorrow Alert'
                                  : msg.message_type === 'today_alert' ? 'Today Alert'
                                  : msg.message_type}
                              </span>
                            )}
                            <span className="text-[10px] text-brand-neutral">{msg.template_name || ''}</span>
                          </div>
                          <p className="text-sm font-medium text-brand-dark truncate">{msg.customer_name || 'Unknown'}</p>
                          <div className="flex flex-wrap gap-x-3 text-[11px] text-brand-neutral mt-0.5">
                            {msg.phone_number && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {msg.phone_number}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {msg.scheduled_date || '-'}
                            </span>
                            <span>#{msg.work_order_number || 'N/A'}</span>
                            {msg.week_start && msg.week_end && (
                              <span className="text-brand-neutral/60">{formatWeekRange(msg.week_start, msg.week_end)}</span>
                            )}
                          </div>
                          {msg.error_message && (
                            <p className="text-[11px] text-red-500 mt-1 truncate">{msg.error_message}</p>
                          )}
                        </div>
                        <span className="text-[11px] text-brand-neutral whitespace-nowrap">
                          {formatDateTime(msg.sent_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {totalMessages > 50 && (
                  <div className="px-4 py-3 border-t border-brand-light flex items-center justify-between text-xs">
                    <span className="text-brand-neutral">
                      Showing {historyPage * 50 + 1}-{Math.min((historyPage + 1) * 50, totalMessages)} of {totalMessages}
                    </span>
                    <div className="flex gap-2">
                      <button
                        disabled={historyPage === 0}
                        onClick={() => setHistoryPage((p) => p - 1)}
                        className="px-3 py-1.5 rounded-lg bg-brand-light text-brand-dark hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-30"
                      >
                        Previous
                      </button>
                      <button
                        disabled={(historyPage + 1) * 50 >= totalMessages}
                        onClick={() => setHistoryPage((p) => p + 1)}
                        className="px-3 py-1.5 rounded-lg bg-brand-light text-brand-dark hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-30"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </Layout>
  );
}
